<template>
  <PopWrap
    ref="choicePopWrap"
    :disabled="btnDisabled"
    :btnText="btnStatus"
    @clicked="handleBtnClick"
  >
    <template v-slot:title>
      <div class="title-wrap">
        <div class="title-label flex items-center text-motif-font-100">
          <img class="title-icon" :src="labelIcon" alt="" />
          {{ labelText }}
        </div>
        <div class="title-content">
          {{ content.title }}
        </div>
      </div>
    </template>
    <div class="choice-content" :class="optionClasses">
      <div
        v-for="(option, index) in content.options"
        :key="index"
        :class="[choiceState(option), 'choice-option']"
        @click="handleOptionClick(option)"
      >
        {{ `${option.mark}. ${option.content}` }}
      </div>
    </div>
    <ChoiceAnalysis
      v-if="isAnalysisShow"
      :analysis="content.analysis"
      :correctAnswer="analysisAnswer"
    />
  </PopWrap>
</template>

<script lang="ts">
/* eslint-disable vue/no-duplicate-attributes */
import { Content } from '@forchange/storytime';
import { Vue, Prop, Component } from 'vue-property-decorator';
import PopWrap from '../VideoPopWrap';
import IconRadio from './assets/icon-radio.svg';
import IconCheckbox from './assets/icon-checkbox.svg';
import ChoiceAnalysis from '../VideoCommon/VideoPopAnalysis.vue';

enum CHOICESTATUS {
  SUBMITTED = 'SUBMITTED',
  CORRECT = 'CORRECT',
  WRONG = 'WRONG'
}

type IOption = { mark: string; content: string };

@Component({
  components: {
    PopWrap,
    ChoiceAnalysis
  }
})
export default class VideoChoice extends Vue {
  @Prop({ type: Object }) content!: Content & {
    title: string;
    options: IOption[];
    answer?: string | string[];
    analysis?: string;
    selectedOptions?: IOption[];
  };
  @Prop() isInLookBack!: boolean;

  selectedOptions: IOption[] = [];
  submitted = false;
  scrollTimer: number | null = null;
  startTime: number;
  goNextDelay = 500;

  get isMuti() {
    return this.content.$kind === 'VideoCheckbox';
  }
  get labelText() {
    return this.isMuti ? '多选题' : '单选题';
  }
  get labelIcon() {
    return this.isMuti ? IconCheckbox : IconRadio;
  }
  get btnStatus() {
    let status = '';
    if (!this.isAutoNext) {
      status = '继续';
    } else if (this.isMuti) {
      status = '确定';
    }

    return status;
  }
  get choiceStatus() {
    if (!this.submitted) return '';
    if (!this.content.answer) return CHOICESTATUS.SUBMITTED;

    if (this.isMuti) {
      return !(this.content.answer as string[]).find(
        (answer) => !this.selectedOptions.find((option) => option.mark === answer)
      ) && this.selectedOptions.length === this.content.answer.length
        ? CHOICESTATUS.CORRECT
        : CHOICESTATUS.WRONG;
    } else {
      return this.selectedOptions[0].mark === this.content.answer
        ? CHOICESTATUS.CORRECT
        : CHOICESTATUS.WRONG;
    }
  }
  get optionClasses() {
    return {
      submitted: this.submitted,
      correct: this.choiceStatus === CHOICESTATUS.CORRECT,
      wrong: this.choiceStatus === CHOICESTATUS.WRONG
    };
  }
  get analysisAnswer() {
    if (!this.content.answer) return;
    return this.isMuti ? this.content.answer : [this.content.answer];
  }
  get isAnalysisShow() {
    return this.submitted && (this.content.analysis || this.content.answer);
  }
  get btnDisabled() {
    return this.selectedOptions.length < 1;
  }

  get isAutoNext() {
    if (this.isInLookBack || this.isAnalysisShow) return false;
    return true;
  }

  choiceState(option: IOption) {
    return this.selectedOptions.find((item) => item.mark === option.mark) ? 'selected' : '';
  }

  handleOptionClick(option: IOption) {
    if (this.submitted) return;
    if (this.isMuti) {
      const oIndex = this.selectedOptions.indexOf(option);
      if (oIndex > -1) {
        this.selectedOptions.splice(oIndex, 1);
      } else {
        this.selectedOptions.push(option);
      }
    } else {
      this.selectedOptions = [option];
      this.submitted = true;
      window.storyteller.input(option.mark);
      this.submit();

      this.handleSubmitted();
    }
  }

  handleBtnClick() {
    if (!this.submitted && this.isMuti) {
      this.submitted = true;
      window.storyteller.input(this.selectedOptions);
      this.submit();
      this.handleSubmitted();
    } else {
      this.$emit('next');
    }
  }

  submit() {
    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        selectedOptions: this.selectedOptions
      }
    });
    this.trackSelection();
  }

  trackSelection() {
    let is_correct: 0 | 1 | 2 = 0;
    if (this.choiceStatus === CHOICESTATUS.CORRECT) {
      is_correct = 1;
    } else if (this.choiceStatus === CHOICESTATUS.WRONG) {
      is_correct = 2;
    }

    window.$tracker?.tracker?.selectionSubmit({
      resource_id: this.content.$id,
      next_resource_id: '',
      content: this.selectedOptions?.map((item) => item.mark || item.content) || [],
      content_value: this.selectedOptions?.map((item) => item.content) || [],
      is_correct,
      duration: new Date().getTime() - this.startTime,
      title: this.content.title,
      mark: this.selectedOptions?.map((item) => item.mark) || []
    });
  }

  handleSubmitted() {
    if (!this.isAnalysisShow) {
      setTimeout(() => this.$emit('next'), this.goNextDelay);
    } else {
      this.scrollTimer = window.setTimeout(() => {
        this.scrollToBottom();
      }, 200);
    }
  }

  scrollToBottom() {
    const wrap = this.$refs.choicePopWrap as PopWrap;
    wrap.scrollToBottom();
  }

  beforeDestroy() {
    this.scrollTimer && clearTimeout(this.scrollTimer);
  }

  mounted() {
    if (this.content.selectedOptions) {
      this.selectedOptions = this.content.selectedOptions;
      this.submitted = true;
      if (this.isAutoNext) {
        setTimeout(() => this.$emit('next'), this.goNextDelay);
      }
    }

    this.startTime = new Date().getTime();
  }
}
</script>

<style lang="scss" scoped>
.title-wrap {
  padding: 5px 20px 20px;
}
.title-label {
  height: 20px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 20px;
}
.title-icon {
  width: 14px;
  margin-right: 4px;
}
.title-content {
  @apply text-motif-font-400 font-medium;
  font-size: 16px;
  line-height: 22px;
}

.choice-option {
  @apply w-full cursor-pointer;
  margin-bottom: 16px;
  padding: 12px 16px;
  color: #808588;
  font-size: 14px;
  line-height: 20px;
  background: #f2f3f7;
  border: 1px solid #f2f3f7;
  border-radius: 8px;
}
.submitted .choice-option {
  cursor: auto;
}
.selected {
  color: #524eff;
  background: rgba(82, 78, 255, 0.08);
  border: 1px solid #524eff;
}
.correct {
  .selected {
    color: #0bb459;
    background: rgba(11, 180, 89, 0.08);
    border: 1px solid #0bb459;
  }
}
.wrong {
  .selected {
    color: #fb3d31;
    background: rgba(251, 61, 49, 0.08);
    border: 1px solid #fb3d31;
  }
}
</style>
